<script setup lang="ts">
import { ref } from 'vue'
import { useRouter } from 'vue-router'

const router = useRouter()
const mode = ref<'login' | 'register'>('login')
const username = ref('')
const password = ref('')
const confirmPassword = ref('')
const isLoading = ref(false)
const errorMsg = ref('')

async function handleSubmit() {
  errorMsg.value = ''
  if (!username.value || !password.value) {
    errorMsg.value = '请填写用户名和密码'
    return
  }
  if (mode.value === 'register' && password.value !== confirmPassword.value) {
    errorMsg.value = '两次密码输入不一致'
    return
  }
  isLoading.value = true
  await new Promise(r => setTimeout(r, 800))
  isLoading.value = false
  router.push('/game/dashboard')
}

function handleGuest() {
  router.push('/game/dashboard')
}
</script>

<template>
  <div class="min-h-screen bg-bg-deep flex items-center justify-center p-4 relative overflow-hidden">
    <!-- 背景光晕层 -->
    <div class="absolute inset-0 pointer-events-none">
      <div class="absolute top-[-10%] left-[-5%] w-[600px] h-[600px] rounded-full bg-primary opacity-[0.06] blur-[120px]" />
      <div class="absolute bottom-[-10%] right-[-5%] w-[500px] h-[500px] rounded-full bg-accent-purple opacity-[0.07] blur-[100px]" />
      <div class="absolute top-[40%] left-[60%] w-[300px] h-[300px] rounded-full bg-accent-cyan opacity-[0.04] blur-[80px]" />
    </div>

    <!-- 背景网格 -->
    <div class="absolute inset-0 pointer-events-none auth-grid-bg" />

    <!-- 星点装饰 -->
    <div class="absolute inset-0 pointer-events-none overflow-hidden">
      <div v-for="i in 20" :key="i" class="star-dot" :style="`top:${Math.random()*100}%;left:${Math.random()*100}%;animation-delay:${Math.random()*3}s;width:${1+Math.random()*2}px;height:${1+Math.random()*2}px`" />
    </div>

    <div class="relative w-full max-w-sm z-10">
      <!-- Logo 区域 -->
      <div class="text-center mb-10">
        <!-- SVG Logo -->
        <div class="inline-flex items-center justify-center mb-5 relative">
          <div class="absolute w-24 h-24 rounded-full bg-primary opacity-20 blur-xl animate-pulse" />
          <div class="relative w-20 h-20 rounded-2xl bg-bg-surface border border-primary border-opacity-30 flex items-center justify-center shadow-glow-pink">
            <svg viewBox="0 0 48 48" width="40" height="40" fill="none" xmlns="http://www.w3.org/2000/svg" aria-label="地下偶像事务所">
              <!-- 月亮 -->
              <path d="M30 8C22.268 8 16 14.268 16 22C16 29.732 22.268 36 30 36C31.6 36 33.13 35.72 34.55 35.21C31.2 33.41 29 29.95 29 26C29 20.477 33.477 16 39 16C39.69 16 40.37 16.07 41 16.21C39.06 11.37 34.92 8 30 8Z" fill="#FF6B9D" opacity="0.9"/>
              <!-- 星星 -->
              <path d="M38 6L38.9 8.9L42 9L39.9 11.1L40.5 14L38 12.5L35.5 14L36.1 11.1L34 9L37.1 8.9Z" fill="#FFD166" />
              <path d="M10 30L10.6 31.9L12.5 32L11.1 33.4L11.5 35.3L10 34.3L8.5 35.3L8.9 33.4L7.5 32L9.4 31.9Z" fill="#B388FF" opacity="0.8"/>
              <!-- 底部横线 -->
              <rect x="12" y="38" width="24" height="2" rx="1" fill="#FF6B9D" opacity="0.5"/>
              <rect x="18" y="42" width="12" height="2" rx="1" fill="#FF6B9D" opacity="0.3"/>
            </svg>
          </div>
        </div>
        <h1 class="text-3xl font-display font-bold text-text-primary tracking-wide">暗星制作</h1>
        <p class="text-text-secondary text-sm mt-2 tracking-widest uppercase">Underground Idol Agency</p>
      </div>

      <!-- 主卡片 -->
      <div class="bg-bg-surface rounded-xl shadow-modal border border-accent-purple border-opacity-20 overflow-hidden">
        <!-- Tab 切换 -->
        <div class="flex bg-bg-base border-b border-accent-purple border-opacity-10">
          <button
            class="flex-1 py-3.5 text-sm font-semibold transition-all relative"
            :class="mode === 'login' ? 'text-primary' : 'text-text-secondary hover:text-text-primary'"
            @click="mode = 'login'; errorMsg = ''"
          >
            登录
            <span v-if="mode === 'login'" class="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-primary rounded-full" />
          </button>
          <button
            class="flex-1 py-3.5 text-sm font-semibold transition-all relative"
            :class="mode === 'register' ? 'text-primary' : 'text-text-secondary hover:text-text-primary'"
            @click="mode = 'register'; errorMsg = ''"
          >
            注册
            <span v-if="mode === 'register'" class="absolute bottom-0 left-1/4 right-1/4 h-0.5 bg-primary rounded-full" />
          </button>
        </div>

        <!-- 表单区 -->
        <div class="p-6">
          <form @submit.prevent="handleSubmit" class="space-y-4">
            <div>
              <label class="block text-text-primary text-sm font-medium mb-1.5">用户名</label>
              <input
                v-model="username"
                type="text"
                placeholder="请输入用户名"
                class="auth-input"
              />
            </div>

            <div>
              <label class="block text-text-primary text-sm font-medium mb-1.5">密码</label>
              <input
                v-model="password"
                type="password"
                placeholder="请输入密码"
                class="auth-input"
              />
            </div>

            <div v-if="mode === 'register'">
              <label class="block text-text-primary text-sm font-medium mb-1.5">确认密码</label>
              <input
                v-model="confirmPassword"
                type="password"
                placeholder="再次输入密码"
                class="auth-input"
              />
            </div>

            <!-- 错误提示 -->
            <p v-if="errorMsg" class="text-danger text-sm flex items-center gap-1.5">
              <span>⚠</span>{{ errorMsg }}
            </p>

            <!-- 提交按钮 -->
            <button
              type="submit"
              :disabled="isLoading"
              class="w-full py-3 bg-primary text-bg-deep font-bold rounded-lg hover:bg-primary-light disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-glow-pink text-sm tracking-wide mt-2"
            >
              <span v-if="isLoading" class="flex items-center justify-center gap-2">
                <svg class="animate-spin w-4 h-4" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"/><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"/></svg>
                {{ mode === 'login' ? '登录中…' : '注册中…' }}
              </span>
              <span v-else>{{ mode === 'login' ? '开始游玩' : '创建账号' }}</span>
            </button>
          </form>

          <!-- 分隔线 -->
          <div class="flex items-center gap-3 my-5">
            <div class="flex-1 h-px bg-bg-elevated" />
            <span class="text-text-secondary text-xs font-medium">或者</span>
            <div class="flex-1 h-px bg-bg-elevated" />
          </div>

          <!-- 游客模式 -->
          <button
            @click="handleGuest"
            class="w-full py-2.5 border border-accent-purple border-opacity-30 text-text-primary text-sm rounded-lg hover:border-accent-purple hover:bg-bg-elevated transition-all font-medium"
          >
            以游客身份游玩
            <span class="text-text-secondary text-xs ml-1">（仅本地存档）</span>
          </button>
        </div>
      </div>

      <p class="text-center text-text-secondary text-xs mt-5 opacity-60">
        UI Demo · 账号系统尚未接入后端
      </p>
    </div>
  </div>
</template>

<style scoped>
.auth-input {
  @apply w-full bg-bg-base border border-bg-elevated rounded-lg px-4 py-2.5 text-text-primary text-sm placeholder-text-disabled focus:outline-none focus:border-primary transition-colors;
}

.auth-grid-bg {
  background-image:
    linear-gradient(rgba(179, 136, 255, 0.03) 1px, transparent 1px),
    linear-gradient(90deg, rgba(179, 136, 255, 0.03) 1px, transparent 1px);
  background-size: 40px 40px;
}

.star-dot {
  position: absolute;
  background: white;
  border-radius: 50%;
  opacity: 0;
  animation: star-twinkle 3s ease-in-out infinite;
}

@keyframes star-twinkle {
  0%, 100% { opacity: 0; transform: scale(0.5); }
  50% { opacity: 0.6; transform: scale(1); }
}
</style>
